import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../constants/providers/AuthProvider';

export interface Note {
  id: string;
  title: string;
  body: string;
  notebook_id: string | null;
  user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Notebook {
  id: string;
  title: string;
  user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Notes ──────────────────────────────────────────────────────────────────

export function useNotes(notebookId?: string | null) {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const query = useQuery({
    queryKey: ['notes', userId, notebookId ?? 'all'],
    queryFn: async (): Promise<Note[]> => {
      if (!userId) return [];

      let q = supabase
        .from('notes')
        .select('id, title, body, notebook_id, user_id, created_at, updated_at')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (notebookId) {
        q = q.eq('notebook_id', notebookId);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as Note[];
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

  return {
    notes: (query.data || []) as Note[],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

export function useCreateNote() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ title, body, notebookId }: { title: string; body: string; notebookId?: string | null }) => {
      if (!userId) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('notes')
        .insert({
          title,
          body,
          user_id: userId,
          notebook_id: notebookId ?? null,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, title, body }: { noteId: string; title: string; body: string }) => {
      const { error } = await supabase
        .from('notes')
        .update({ title, body, updated_at: new Date().toISOString() })
        .eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase.from('notes').delete().eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useMoveNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, notebookId }: { noteId: string; notebookId: string | null }) => {
      const { error } = await supabase
        .from('notes')
        .update({ notebook_id: notebookId, updated_at: new Date().toISOString() })
        .eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

// ─── Notebooks ──────────────────────────────────────────────────────────────

export function useNotebooks() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const query = useQuery({
    queryKey: ['notebooks', userId],
    queryFn: async (): Promise<Notebook[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('notebooks')
        .select('id, title, user_id, created_at, updated_at')
        .eq('user_id', userId)
        .neq('id', '00000000-0000-0000-0000-000000000000')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as Notebook[];
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });

  return {
    notebooks: (query.data || []) as Notebook[],
    isLoading: query.isLoading,
  };
}

export function useCreateNotebook() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (title: string) => {
      if (!userId) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('notebooks')
        .insert({ title, user_id: userId })
        .select('id, title')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
}

export function useRenameNotebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ notebookId, title }: { notebookId: string; title: string }) => {
      const { error } = await supabase
        .from('notebooks')
        .update({ title, updated_at: new Date().toISOString() })
        .eq('id', notebookId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
    },
  });
}

export function useDeleteNotebook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notebookId: string) => {
      // Move notes to "no notebook" before deleting
      await supabase
        .from('notes')
        .update({ notebook_id: null })
        .eq('notebook_id', notebookId);

      const { error } = await supabase.from('notebooks').delete().eq('id', notebookId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}
